// ds-typo-ignore-file — mockup simulates Instagram UI, not marketing site DS
"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import { videoPosterUrl } from "@/lib/video-poster"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

export interface InstagramTestimonialProps {
  /** Short muted loop video */
  previewSrc: string
  /** Full video with audio (played on click) */
  fullSrc: string
  /** Poster image shown before lazy-load */
  posterSrc: string
  /** Instagram handle without @ */
  handle: string
  /** Formatted follower count, e.g. "12.5K" */
  followers: string
  /** Optional short caption excerpt */
  caption?: string
  className?: string
  /** Payload testimonial doc id — registers the inline-editor regions for the
   *  `instagramHandle`, `instagramFollowers`, and `quote` (caption) fields.
   *  Absent → no regions register. */
  cmsId?: string
  /** BCP47 CMS locale these docs were fetched in (region provenance). */
  editLocale?: string
}

function InstagramIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 1 0 0-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 0 1-2.88 0 1.441 1.441 0 0 1 2.88 0z" />
    </svg>
  )
}

export function InstagramTestimonial({
  previewSrc,
  fullSrc,
  posterSrc,
  handle,
  followers,
  caption,
  className,
  cmsId,
  editLocale,
}: InstagramTestimonialProps) {
  const ut = useTranslations("ui")
  const handleRef = useEditRegion<HTMLSpanElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "instagramHandle",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const followersRef = useEditRegion<HTMLSpanElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "instagramFollowers",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const captionRef = useEditRegion<HTMLSpanElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "quote",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLVideoElement>(null)
  const fullRef = useRef<HTMLVideoElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)

  // Lazy-load: only start preview when scrolled into view
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          obs.unobserve(el)
        }
      },
      { threshold: 0.3 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const playFull = () => {
    setIsPlaying(true)
    setTimeout(() => {
      const vid = fullRef.current
      if (vid) {
        vid.currentTime = 0
        vid.muted = false
        vid.play().catch(() => {
          vid.muted = true
          vid.play().catch(() => {})
        })
      }
    }, 50)
  }

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Video area */}
      <div
        ref={containerRef}
        className="relative aspect-[9/16] flex-auto overflow-hidden rounded-[12px] bg-neutral-200"
      >
        {!isPlaying ? (
          <>
            {/* Muted autoplay loop preview */}
            {isVisible ? (
              <video
                ref={previewRef}
                src={previewSrc}
                poster={videoPosterUrl(posterSrc, 300, 533)}
                muted
                autoPlay
                loop
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <Image
                src={posterSrc}
                alt=""
                fill
                sizes="(min-width: 768px) 300px, 50vw"
                className="object-cover"
              />
            )}

            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

            {/* Instagram badge (top-left) */}
            <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full bg-black/30 px-3 py-1.5 backdrop-blur-sm">
              <InstagramIcon size={16} />
              <span className="text-[13px] font-medium text-white">
                Instagram
              </span>
            </div>

            {/* Play button */}
            <button
              type="button"
              onClick={playFull}
              className="group absolute inset-0 flex cursor-pointer items-center justify-center"
              aria-label={`Play Instagram video from @${handle}`}
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-colors group-hover:bg-white/30">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="white"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </button>
          </>
        ) : (
          /* Full video with controls */
          <video
            ref={fullRef}
            src={fullSrc}
            controls
            playsInline
            data-exclusive-audio="true"
            onPlay={(e) => {
              const self = e.currentTarget
              document
                .querySelectorAll<HTMLVideoElement>(
                  'video[data-exclusive-audio="true"]'
                )
                .forEach((v) => {
                  if (v !== self) v.pause()
                })
            }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        {/* Handle + followers overlay (always visible) */}
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 p-4">
          <div className="flex items-center gap-2">
            <div className="text-white">
              <InstagramIcon size={16} />
            </div>
            {/* Stamp only the bare handle/count, not the `@` or " followers"
                literal, so the inline editor seeds + saves the raw CMS value. */}
            <span className="text-[15px] font-medium text-white">
              @<span ref={handleRef}>{handle}</span>
            </span>
            <span className="text-[13px] text-white/60">&middot;</span>
            <span className="text-[13px] text-white/60">
              <span ref={followersRef}>{followers}</span> followers
            </span>
          </div>
        </div>
      </div>

      {/* Caption below video */}
      {caption && (
        <p className="mt-3 line-clamp-2 px-1 text-[14px] leading-snug text-black/60">
          &ldquo;<span ref={captionRef}>{caption}</span>&rdquo;
        </p>
      )}
      {/* Announce the preview→full-video swap to screen readers. */}
      <div role="status" aria-live="polite" className="sr-only">
        {isPlaying ? `${ut("videoPlaying")}: @${handle}` : ""}
      </div>
    </div>
  )
}
